import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAdmin } from '../hooks/useAdmin';

interface AdminRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
}

export function AdminRoute({ children, requiredPermission }: AdminRouteProps) {
  const { isAdmin, hasPermission, loading, error } = useAdmin();
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [hasRequiredPermission, setHasRequiredPermission] = useState(false);
  const [permissionError, setPermissionError] = useState<Error | null>(null);

  // Loggons l'état actuel pour déboguer
  useEffect(() => {
    console.log('[AdminRoute] État actuel:', {
      isAdmin,
      loading,
      requiredPermission,
      hasError: !!error,
      permissionChecked
    });
  }, [isAdmin, loading, requiredPermission, error, permissionChecked]);

  // Vérification de permission séparée pour éviter les boucles infinies
  useEffect(() => {
    if (!loading && isAdmin && requiredPermission) {
      try {
        const result = hasPermission(requiredPermission);
        console.log(`[AdminRoute] Vérification permission ${requiredPermission}: ${result}`);
        setHasRequiredPermission(result);
      } catch (err) {
        console.error(`[AdminRoute] Erreur lors de la vérification de la permission ${requiredPermission}:`, err);
        setPermissionError(err instanceof Error ? err : new Error('Erreur inconnue'));
      } finally {
        setPermissionChecked(true);
      }
    } else if (!loading && isAdmin) {
      // Si pas de permission requise, considérer comme vérifié
      setPermissionChecked(true);
      setHasRequiredPermission(true);
    }
  }, [isAdmin, loading, requiredPermission, hasPermission]);

  // Attendre que les informations d'admin soient chargées
  if (loading) {
    return (
      <div 
        className="min-h-screen bg-black flex items-center justify-center"
        role="status"
        aria-label="Chargement en cours"
      >
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-4 border-primary-end/20"></div>
          <div 
            className="absolute inset-0 rounded-full border-4 border-t-primary-end animate-spin"
            aria-hidden="true"
          ></div>
        </div>
      </div>
    );
  }

  // S'il y a une erreur lors du chargement des informations admin
  if (error) {
    console.error('[AdminRoute] Erreur:', error);
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 text-center">
        <h1 className="text-2xl font-bold text-white mb-4">Erreur</h1>
        <p className="text-gray-400 mb-6">Une erreur est survenue lors du chargement des informations d'administration.</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-gradient-to-r from-primary-start to-primary-end text-white px-6 py-2 rounded-xl"
        >
          Réessayer
        </button>
      </div>
    );
  }

  // Si l'utilisateur n'est pas admin, rediriger vers la page d'accueil
  if (!isAdmin) {
    console.log('[AdminRoute] Utilisateur non admin, redirection');
    return <Navigate to="/" replace />;
  }

  // Si une permission spécifique est requise et que la vérification est terminée
  if (requiredPermission && permissionChecked) {
    if (permissionError) {
      console.error('[AdminRoute] Erreur de permission:', permissionError);
      return <Navigate to="/admin" replace />;
    }
    
    if (!hasRequiredPermission) {
      console.log('[AdminRoute] Permission refusée, redirection vers admin');
      return <Navigate to="/admin" replace />;
    }
  }

  // Rendre les enfants (la page admin) si tout est OK
  console.log('[AdminRoute] Accès autorisé');
  return <>{children}</>;
} 